/**
 * Superadmin portal (H) — a separate, obscure-URL portal whose ONLY job is
 * creating instances + provisioning their owners. The superadmin does not
 * operate instances. Path is set by VITE_ADMIN_PATH at build (obscurity is a
 * thin layer — the real gate is superadmin auth, enforced server-side).
 */

import { useEffect, useState } from "react";
import { getMe, login, logout, type Me } from "../auth";
import { CreateInstanceModal } from "../components/CreateInstanceModal";

export function AdminPortal() {
  const [me, setMe] = useState<Me | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [created, setCreated] = useState<string | null>(null);

  const refresh = () => getMe().then(setMe).catch(() => setMe({ authenticated: false }));
  useEffect(() => {
    let alive = true;
    getMe().then(m => alive && setMe(m)).catch(() => alive && setMe({ authenticated: false }));
    return () => { alive = false; };
  }, []);

  if (me === null) return <Center><div style={S.muted}>Loading…</div></Center>;
  if (!me.authenticated) return <AdminLogin onAuthed={refresh} />;
  if (!me.is_superadmin) {
    return (
      <Center>
        <div style={S.card}>
          <h1 style={S.title}>Restricted</h1>
          <p style={S.muted}>This portal is superadmin-only. You're signed in as <strong>{me.email}</strong>.</p>
          <button style={S.ghost} onClick={async () => { await logout(); refresh(); }}>Sign out</button>
        </div>
      </Center>
    );
  }

  return (
    <div style={S.dash}>
      <div style={S.bar}>
        <span style={{ fontWeight: 700 }}>Nexus · Superadmin</span>
        <span style={{ flex: 1 }} />
        <span style={S.who}>{me.email}</span>
        <button style={S.ghost} onClick={async () => { await logout(); refresh(); }}>Sign out</button>
      </div>
      <div style={S.body}>
        <h1 style={S.title}>Instance control</h1>
        <p style={S.muted}>
          Create an instance and provision its owner (email + initial password).
          The owner signs in at the main app and runs it — you don't operate instances here.
        </p>
        {created && (
          <div style={S.ok}>
            ✓ Instance <strong>{created}</strong> created. The owner can sign in at the main app now.
          </div>
        )}
        <button style={S.primary} onClick={() => { setCreated(null); setModalOpen(true); }}>+ Create instance</button>
      </div>
      <CreateInstanceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(slug) => { setModalOpen(false); setCreated(slug); }}
      />
    </div>
  );
}

function AdminLogin({ onAuthed }: { onAuthed: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await login(email.trim(), password);
      onAuthed();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <Center>
      <div style={S.card}>
        <h1 style={S.title}>Superadmin</h1>
        <p style={S.muted}>Restricted portal — sign in.</p>
        <form onSubmit={submit}>
          <input style={S.input} type="email" placeholder="superadmin email" value={email}
                 onChange={e => setEmail(e.target.value)} autoComplete="username" autoFocus required />
          <input style={S.input} type="password" placeholder="Password" value={password}
                 onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
          <button style={S.primary} type="submit" disabled={busy}>{busy ? "…" : "Sign in"}</button>
          {error && <div style={S.err}>{error}</div>}
        </form>
      </div>
    </Center>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return <div style={S.center}>{children}</div>;
}

const S: Record<string, React.CSSProperties> = {
  center: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--page)", fontFamily: "system-ui, sans-serif" },
  card: { width: 360, padding: 32, background: "var(--surface)", borderRadius: 14, border: "1px solid var(--line-strong)", color: "var(--ink)", boxShadow: "var(--shadow-overlay)" },
  dash: { minHeight: "100vh", background: "var(--page)", color: "var(--ink)", fontFamily: "system-ui, sans-serif" },
  bar: { display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderBottom: "1px solid var(--line)", fontSize: 14 },
  who: { color: "var(--ink-2)", fontSize: 13 },
  body: { maxWidth: 560, margin: "48px auto", padding: "0 24px" },
  title: { margin: "0 0 8px", fontSize: 24, fontWeight: 700 },
  muted: { color: "var(--ink-2)", fontSize: 14, lineHeight: 1.55, margin: "0 0 16px" },
  input: { width: "100%", boxSizing: "border-box", padding: "10px 12px", margin: "10px 0 0", background: "var(--surface)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 14 },
  primary: { padding: "10px 16px", marginTop: 6, background: "var(--accent)", color: "var(--accent-fg)", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  ghost: { padding: "6px 12px", background: "transparent", color: "var(--ink)", border: "1px solid var(--line-strong)", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  ok: { padding: 12, background: "var(--green-tint)", border: "1px solid color-mix(in srgb, var(--green) 30%, transparent)", borderRadius: 8, color: "var(--green)", fontSize: 14, marginBottom: 16 },
  err: { fontSize: 13, color: "var(--red)", marginTop: 10 },
};
